import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { parseCoordsFromMapsUrl } from './map-coords.js';
import { trapModalHistory } from './modal-history.js';

let pickerModal = null;
let dismissHistory = null;

function pickerMarkerIcon() {
  return L.divIcon({
    className: 'map-pin map-pin--picker',
    html: '<span class="map-pin__badge map-pin__badge--picker">●</span>',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function syncCoordsToForm(form, lat, lng) {
  const latHidden = form.querySelector('[name="lat"]');
  const lngHidden = form.querySelector('[name="lng"]');
  const latVisible = form.querySelector('[data-place-lat-visible]');
  const lngVisible = form.querySelector('[data-place-lng-visible]');
  if (latHidden) latHidden.value = lat != null ? String(lat) : '';
  if (lngHidden) lngHidden.value = lng != null ? String(lng) : '';
  if (latVisible) latVisible.value = lat != null ? String(lat) : '';
  if (lngVisible) lngVisible.value = lng != null ? String(lng) : '';
}

function readCoordsFromForm(form) {
  const lat = Number(form.querySelector('[name="lat"]')?.value || form.querySelector('[data-place-lat-visible]')?.value);
  const lng = Number(form.querySelector('[name="lng"]')?.value || form.querySelector('[data-place-lng-visible]')?.value);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  const mapsUrl = form.querySelector('[name="mapsUrl"]')?.value;
  return parseCoordsFromMapsUrl(mapsUrl) ?? null;
}

function destroyMapPicker() {
  pickerModal?.remove();
  pickerModal = null;
}

function destroyMapPickerFromBack() {
  dismissHistory = null;
  destroyMapPicker();
}

function closeMapPicker() {
  const dismiss = dismissHistory;
  dismissHistory = null;
  if (dismiss) dismiss();
  else destroyMapPicker();
}

function openMapPicker(form, { defaultCenter = [46.15, 14.5], defaultZoom = 8 } = {}) {
  closeMapPicker();
  const existing = readCoordsFromForm(form);

  pickerModal = document.createElement('div');
  pickerModal.className = 'place-picker-modal';
  pickerModal.innerHTML = `
    <div class="place-picker-modal__backdrop" data-close-picker></div>
    <div class="place-picker-modal__panel" role="dialog" aria-label="Seleziona sulla mappa">
      <header class="place-picker-modal__head">
        <h2 class="place-picker-modal__title">Seleziona sulla mappa</h2>
        <button type="button" class="btn-ghost btn-ghost--sm" data-close-picker>Chiudi</button>
      </header>
      <p class="place-picker-modal__hint">Tocca un punto sulla mappa per impostare le coordinate.</p>
      <div id="place-picker-map" class="place-picker-modal__map"></div>
      <p class="place-picker-modal__coords" id="place-picker-coords">—</p>
      <button type="button" class="btn-primary place-picker-modal__confirm" id="place-picker-confirm" disabled>Usa questo punto</button>
    </div>
  `;
  document.body.appendChild(pickerModal);
  dismissHistory = trapModalHistory(destroyMapPickerFromBack);

  let selected = existing;
  const coordsEl = pickerModal.querySelector('#place-picker-coords');
  const confirmBtn = pickerModal.querySelector('#place-picker-confirm');

  function updateCoordsLabel() {
    if (selected) {
      coordsEl.textContent = `${selected.lat.toFixed(5)}, ${selected.lng.toFixed(5)}`;
      confirmBtn.disabled = false;
    } else {
      coordsEl.textContent = 'Nessun punto selezionato';
      confirmBtn.disabled = true;
    }
  }

  const mapEl = pickerModal.querySelector('#place-picker-map');
  const map = L.map(mapEl, { zoomControl: true }).setView(
    existing ? [existing.lat, existing.lng] : defaultCenter,
    existing ? 14 : defaultZoom,
  );
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; OSM',
  }).addTo(map);

  let marker = null;
  if (existing) {
    marker = L.marker([existing.lat, existing.lng], { icon: pickerMarkerIcon() }).addTo(map);
    updateCoordsLabel();
  }

  map.on('click', (e) => {
    selected = { lat: e.latlng.lat, lng: e.latlng.lng };
    if (marker) marker.setLatLng(e.latlng);
    else marker = L.marker(e.latlng, { icon: pickerMarkerIcon() }).addTo(map);
    updateCoordsLabel();
  });

  requestAnimationFrame(() => map.invalidateSize());

  pickerModal.querySelectorAll('[data-close-picker]').forEach((el) => {
    el.addEventListener('click', closeMapPicker);
  });

  confirmBtn.addEventListener('click', () => {
    if (selected) syncCoordsToForm(form, selected.lat, selected.lng);
    closeMapPicker();
  });
}

export function bindPlaceEditor(form, options = {}) {
  if (!form || form.dataset.placeEditorBound) return;
  form.dataset.placeEditorBound = '1';

  const mapsInput = form.querySelector('[data-place-maps-url], [name="mapsUrl"]');

  mapsInput?.addEventListener('blur', () => {
    const coords = parseCoordsFromMapsUrl(mapsInput.value);
    if (coords) syncCoordsToForm(form, coords.lat, coords.lng);
  });

  form.querySelector('[data-place-lat-visible]')?.addEventListener('change', (e) => {
    const lat = Number(e.target.value);
    const lng = Number(form.querySelector('[data-place-lng-visible]')?.value);
    if (Number.isFinite(lat) && Number.isFinite(lng)) syncCoordsToForm(form, lat, lng);
  });

  form.querySelector('[data-place-lng-visible]')?.addEventListener('change', (e) => {
    const lng = Number(e.target.value);
    const lat = Number(form.querySelector('[data-place-lat-visible]')?.value);
    if (Number.isFinite(lat) && Number.isFinite(lng)) syncCoordsToForm(form, lat, lng);
  });

  form.querySelector('[data-place-map-picker]')?.addEventListener('click', (e) => {
    e.preventDefault();
    openMapPicker(form, options);
  });
}

export function placeCoordFieldsHtml(values = {}) {
  return `
    <div class="custom-form__row">
      <label class="field">
        <span class="field-label">Latitudine</span>
        <input class="field-input" name="latVisible" type="number" step="any" placeholder="46.05" value="${values.lat != null ? values.lat : ''}" data-place-lat-visible />
      </label>
      <label class="field">
        <span class="field-label">Longitudine</span>
        <input class="field-input" name="lngVisible" type="number" step="any" placeholder="14.50" value="${values.lng != null ? values.lng : ''}" data-place-lng-visible />
      </label>
    </div>
    <button type="button" class="btn-ghost btn-ghost--sm" data-place-map-picker>📍 Scegli sulla mappa</button>
  `;
}
